"use client";

import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import {cn} from '../../Utils/utils'
import { FiSearch } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { IoIosArrowDown } from "react-icons/io";
import { CiSquareCheck } from "react-icons/ci";

const MaSearchableSelect = forwardRef(
  (
    {
      options = [],
      value,
      onChange,
      placeholder = "Select an option…",
      searchPlaceholder = "Search…",
      emptyMessage = "No results found.",
      disabled = false,
      className,
      label,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // ---- Filter options ----
    const filtered = useMemo(() => {
      return options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
      );
    }, [options, search]);

    const selected = options.find((o) => o.value === value);

    // ---- Close outside click ----
    useEffect(() => {
      const handler = (e) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target)
        ) {
          setOpen(false);
          setSearch("");
        }
      };

      document.addEventListener("mousedown", handler);
      return () =>
        document.removeEventListener("mousedown", handler);
    }, []);

    // ---- Focus search when open ----
    useEffect(() => {
      if (open) inputRef.current?.focus();
    }, [open]);

    const handleSelect = (val) => {
      onChange?.(val);
      setOpen(false);
      setSearch("");
    };

    const handleClear = (e) => {
      e.stopPropagation();
      onChange?.("");
      setSearch("");
    };

    return (
      <div ref={ref} className={cn("relative w-full", className)}>
        {label && (
          <label className="mb-1.5 block text-[13px] font-[600] text-foreground">
            {label}
          </label>
        )}

        <div ref={containerRef}>
          {/* Trigger */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen((prev) => !prev)}
            className={cn(
              "flex w-full items-center justify-between rounded-lg border border-solid border-[#d5d5d5] bg-background px-3 py-2.5 text-sm shadow-sm transition-all",
              "hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring",
              "disabled:cursor-not-allowed disabled:opacity-50 h-[50px] bg-white",
              open && "ring-2 ring-ring/30 border-ring"
            )}
          >
            <span
              className={cn(
                "truncate",
                selected ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {selected?.label || placeholder}
            </span>

            <div className="flex items-center gap-1">
              {selected && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={handleClear}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <IoClose className="h-3.5 w-3.5" />
                </span>
              )}

              <IoIosArrowDown
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                  open && "rotate-180"
                )}
              />
            </div>
          </button>

          {/* Dropdown */}
          {open && (
            <div className="absolute z-50 mt-1.5 w-full animate-in fade-in-0 zoom-in-95 rounded-lg border border-solid border-[#d5d5d5] bg-white shadow-lg">
              
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 " style={{borderBottom:'1px solid #d5d5d5'}}>
                <FiSearch className="h-4 w-4 shrink-0 text-muted-foreground" />

                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none border-none"
                />
              </div>

              {/* Options */}
              <div className="max-h-56 overflow-y-auto p-1">
                {filtered.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {emptyMessage}
                  </p>
                ) : (
                  filtered.map((option) => (
                    <span
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm",
                        "hover:bg-accent hover:text-accent-foreground",
                        "disabled:pointer-events-none disabled:opacity-50",
                        option.value === value &&
                          "bg-accent text-accent-foreground font-medium"
                      )}
                    >
                      <CiSquareCheck
                        className={cn(
                          "h-4 w-4 shrink-0",
                          option.value === value
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      {option.label}
                    </span>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

MaSearchableSelect.displayName = "MaSearchableSelect";

export default MaSearchableSelect;